import { memo } from 'react';
import PropTypes from 'prop-types';
import Checkbox from './Checkbox.jsx';
import RadioButton from './RadioButton.jsx';
import Badge from './Badge.jsx';
import IconHighlight from './IconHighlight.jsx';

/**
 * Borderless selectable row — designed to slot inside {@link CartContainer}.
 *
 * Supports two icon variants:
 * - `icon` — a Lucide icon component (e.g. documents, files).
 * - `iconHighlight` — an {@link IconHighlight} props object (e.g. bundles, entities with color).
 *
 * Figma DS AIMS OS — Governance node 718-2237 (documents) and 718-2330 (bundles).
 *
 * @example — document row
 * <CartContainer size="S" selected={selected} onClick={onSelect}>
 *   <SelectableListItem
 *     label="Q1_Enterprise_Contract.pdf"
 *     icon={FileText}
 *     iconClassName="text-red-400"
 *     checked={selected}
 *   />
 * </CartContainer>
 *
 * @example — bundle row
 * <CartContainer size="S" selected={selected} onClick={onSelect}>
 *   <SelectableListItem
 *     label="Enterprise Contracts Bundle"
 *     iconHighlight={{ icon: Layers, bgColor: "Success", iconColor: "Success" }}
 *     code="BU-001"
 *     checked={selected}
 *   />
 * </CartContainer>
 *
 * The selection interaction is owned by the parent {@link CartContainer} (role="button",
 * tabIndex, onKeyDown). The Checkbox / RadioButton inside are visual-only with
 * `pointer-events-none` to avoid double-firing the click handler.
 *
 * @param {object} props
 * @param {string} [props.label=''] - Main label text (truncated).
 * @param {import('react').ElementType} [props.icon] - Lucide icon component.
 * @param {string} [props.iconClassName=''] - Extra classes for the simple icon.
 * @param {number} [props.iconSize=14] - Size in px for the simple icon.
 * @param {object} [props.iconHighlight] - Props forwarded to {@link IconHighlight} (overrides `icon`).
 * @param {string} [props.code] - Optional code badge (e.g. "BU-001") rendered before the label.
 * @param {string} [props.codeVariant] - {@link Badge} variant for the code badge (default `'informative'`).
 * @param {'checkbox'|'radio'} [props.selectionType='checkbox'] - Indicator type.
 * @param {boolean} [props.checked=false] - Whether the item is selected.
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.className=''] - Extra classes on the root div.
 * @param {string} [props.labelClassName=''] - Extra classes on the text span.
 */
const SelectableListItem = memo(({
  label = '',
  icon: Icon,
  iconClassName = '',
  iconSize = 14,
  iconHighlight,
  code,
  codeVariant = 'informative',
  selectionType = 'checkbox',
  checked = false,
  disabled = false,
  className = '',
  labelClassName = '',
}) => {
  return (
    <div
      className={[
        'flex min-w-0 flex-1 items-center gap-2.5',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/*
       * pointer-events-none: CartContainer (role="button") owns the click.
       * The Checkbox / RadioButton render the correct checked + disabled visuals.
       */}
      <span className="pointer-events-none shrink-0 flex items-center">
        {selectionType === 'radio' ? (
          <RadioButton size="m" checked={checked} disabled={disabled} />
        ) : (
          <Checkbox size="small" checked={checked} disabled={disabled} />
        )}
      </span>

      {iconHighlight ? (
        <IconHighlight
          size="small"
          {...iconHighlight}
          className={['shrink-0', iconHighlight.className].filter(Boolean).join(' ')}
        />
      ) : Icon ? (
        <Icon
          size={iconSize}
          className={['shrink-0', iconClassName].filter(Boolean).join(' ')}
          aria-hidden
        />
      ) : null}

      {code && (
        <Badge size="small" variant={codeVariant} className="shrink-0 font-mono">
          {code}
        </Badge>
      )}

      <span
        className={['min-w-0 truncate text-xs text-content-secondary', labelClassName]
          .filter(Boolean)
          .join(' ')}
        title={label}
      >
        {label}
      </span>
    </div>
  );
});

SelectableListItem.propTypes = {
  label: PropTypes.string,
  icon: PropTypes.elementType,
  iconClassName: PropTypes.string,
  iconSize: PropTypes.number,
  iconHighlight: PropTypes.object,
  code: PropTypes.string,
  codeVariant: PropTypes.string,
  selectionType: PropTypes.oneOf(['checkbox', 'radio']),
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
};

SelectableListItem.displayName = 'SelectableListItem';

export default SelectableListItem;
